/**
 * Map Component
 * Renders the Leaflet map with esri-leaflet layers
 */

import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet.markercluster';
import * as esri from 'esri-leaflet';
import { mapConfig, arcgisConfig, featureServices, symbologyConfig } from '../config/gisConfig';
import { milesToMeters } from '../services/gisService';
import MapLegend from './MapLegend';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

// Fix for default marker icons in Leaflet with React
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

function Map({ queryResults, onFeatureClick, symbologyOverrides = { tractTypes: {}, wellStatuses: {} } }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef({});
  const markersRef = useRef(null);
  const polygonsRef = useRef(null); // Separate layer for polygons (not clustered)
  const labelsLayerRef = useRef(null); // Layer group for labels
  const [layerVisibility, setLayerVisibility] = useState({});

  // Debug: Log layer visibility state changes
  useEffect(() => {
    console.log('📊 Layer visibility state:', layerVisibility);
  }, [layerVisibility]);

  // Auto-select layer when query results arrive
  useEffect(() => {
    if (queryResults && queryResults.layer) {
      const layer = queryResults.layer;

      // Handle multi-layer results
      if (layer === 'multi' && queryResults.layers) {
        console.log(`🌟 Auto-selecting multi-layers: ${queryResults.layers.join(', ')}`);
        const newVisibility = {};
        queryResults.layers.forEach(l => {
          newVisibility[l] = true;
        });
        setLayerVisibility(prev => {
          const updated = { ...prev, ...newVisibility };
          console.log('🔄 Setting multi-layer visibility:', updated);
          return updated;
        });
        return;
      }

      // Map 'proximity' and 'tpl-tracts' to 'tracts'
      const normalizedLayer = layer === 'proximity' || layer === 'tpl-tracts' ? 'tracts' : layer;

      if (featureServices[normalizedLayer]) {
        console.log(`🎯 Auto-selecting layer: ${normalizedLayer}`);
        setLayerVisibility(prev => {
          const updated = { ...prev, [normalizedLayer]: true };
          console.log('🔄 Setting layer visibility:', updated);
          return updated;
        });
      }
    } else if (queryResults === null) {
      // Clear was pressed - uncheck all layers
      console.log('🧹 Clearing layer visibility');
      setLayerVisibility({});
    }
  }, [queryResults]);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    // Create map
    const map = L.map(mapContainerRef.current, {
      center: mapConfig.center,
      zoom: mapConfig.zoom,
      minZoom: mapConfig.minZoom,
      maxZoom: mapConfig.maxZoom
    });

    // Add ArcGIS basemap using tile service
    if (arcgisConfig.apiKey) {
      // Use ArcGIS World Topo Map tiles directly (works with API key)
      L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}', {
        attribution: 'Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ, TomTom, Intermap, iPC, USGS, FAO, NPS, NRCAN, GeoBase, Kadaster NL, Ordnance Survey, Esri Japan, METI, Esri China (Hong Kong), and the GIS User Community',
        maxZoom: 18,
        subdomains: ['server']
      }).addTo(map);
    } else {
      // Fallback to OpenStreetMap (no key needed)
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      }).addTo(map);
    }

    // Feature layers are loaded only when queried (not as persistent layers)
    // This avoids 400 errors from public services that don't support tile requests
    Object.keys(featureServices).forEach(layerKey => {
      setLayerVisibility(prev => ({ ...prev, [layerKey]: false }));
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [onFeatureClick]);

  // Handle window resize manually when needed
  // Removed automatic resize observer to prevent markers from clearing
  // Map has fixed height (600px) so doesn't need automatic invalidateSize

  // Handle query results
  useEffect(() => {
    console.log('Map received queryResults:', queryResults);

    if (!mapRef.current) {
      console.log('Skipping map update: Map not initialized');
      return;
    }

    // Handle explicit null (CLEAR button pressed)
    if (queryResults === null) {
      console.log('🧹 Clearing map: CLEAR button pressed');

      // Remove all layers
      if (markersRef.current) {
        mapRef.current.removeLayer(markersRef.current);
        markersRef.current = null;
      }
      if (polygonsRef.current) {
        mapRef.current.removeLayer(polygonsRef.current);
        polygonsRef.current = null;
      }
      if (labelsLayerRef.current) {
        mapRef.current.removeLayer(labelsLayerRef.current);
        labelsLayerRef.current = null;
      }
      return;
    }

    if (!queryResults) {
      console.log('Skipping map update: queryResults undefined');
      return;
    }

    const { features, extent, zoom, layer, proximityData } = queryResults;
    console.log('Processing map update:', {
      featuresCount: features?.length,
      hasExtent: !!extent,
      zoom,
      layer,
      proximityData: !!proximityData
    });

    // Handle proximity queries specially
    const isProximityQuery = layer === 'proximity' && proximityData;

    // Clear previous markers, polygons, and labels
    if (markersRef.current) {
      mapRef.current.removeLayer(markersRef.current);
    }
    if (polygonsRef.current) {
      mapRef.current.removeLayer(polygonsRef.current);
    }
    if (labelsLayerRef.current) {
      mapRef.current.removeLayer(labelsLayerRef.current);
    }

    // Create polygon layer (always visible, not clustered)
    const polygonsGroup = L.layerGroup().addTo(mapRef.current);
    polygonsRef.current = polygonsGroup;

    // Create labels layer group (will be added/removed based on zoom)
    const labelsLayer = L.layerGroup();
    labelsLayerRef.current = labelsLayer;

    // Create markers layer with clustering (only for pin markers)
    const markersGroup = L.markerClusterGroup({
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      spiderfyOnMaxZoom: true,
      removeOutsideVisibleBounds: true,
      maxClusterRadius: 80,
      disableClusteringAtZoom: 15, // Show individual markers at zoom level 15+
      iconCreateFunction: function(cluster) {
        const count = cluster.getChildCount();
        let className = 'marker-cluster-';
        if (count < 5) {
          className += 'small';
        } else if (count < 10) {
          className += 'medium';
        } else {
          className += 'large';
        }
        return L.divIcon({
          html: `<div><span>${count}</span></div>`,
          className: 'marker-cluster ' + className,
          iconSize: L.point(40, 40)
        });
      }
    }).addTo(mapRef.current);
    markersRef.current = markersGroup;

    // Draw proximity radius circles if this is a proximity query
    if (isProximityQuery && proximityData.centerTracts) {
      const { centerTracts, radius, unit } = proximityData;
      const radiusInMeters = unit === 'kilometers' ? radius * 1000 : milesToMeters(radius);

      // Draw a circle for each center tract
      centerTracts.forEach((centerTractData, idx) => {
        const centerLatLon = [centerTractData.centroid.lat, centerTractData.centroid.lon];

        const circle = L.circle(centerLatLon, {
          radius: radiusInMeters,
          color: '#4ECDC4',
          fillColor: '#4ECDC4',
          fillOpacity: 0.1,
          weight: 2,
          dashArray: '5, 10'
        }).addTo(polygonsGroup);

        console.log(`Drew proximity circle ${idx + 1}:`, {
          center: centerLatLon,
          radius: radius + ' ' + unit,
          radiusInMeters
        });
      });
    }

    // Add center tracts if this is a proximity query
    if (isProximityQuery && proximityData.centerTracts) {
      const { centerTracts } = proximityData;

      centerTracts.forEach((centerTractData, idx) => {
        const centerFeature = centerTractData.feature;
        const props = centerFeature.attributes || centerFeature.properties || {};
        const displayName = props.TractID || props.WellName || `Center Feature ${idx + 1}`;
        const rings = centerFeature.geometry.rings;

        // Convert rings to Leaflet format
        const latlngs = rings.map(ring =>
          ring.map(coord => [coord[1], coord[0]]) // Swap [lon, lat] to [lat, lon]
        );

        // Create center tract polygon with special styling (blue)
        const centerPolygon = L.polygon(latlngs, {
          color: '#2196F3',
          fillColor: '#2196F3',
          fillOpacity: 0.3,
          weight: 3
        }).addTo(polygonsGroup);

        centerPolygon.on('click', () => {
          if (onFeatureClick) {
            onFeatureClick(centerFeature, 'tracts');
          }
        });

        // Add center pin marker
        const centroid = centerPolygon.getBounds().getCenter();
        const centerPinMarker = L.marker(centroid, {
          icon: L.icon({
            iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
            shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.4/images/marker-shadow.png',
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41]
          })
        }).addTo(markersGroup);

        centerPinMarker.on('click', () => {
          if (onFeatureClick) {
            onFeatureClick(centerFeature, 'tracts');
          }
        });

        // Add center tract label
        const centerLabel = L.marker(centroid, {
          icon: L.divIcon({
            className: 'tract-label',
            html: `<div style="background: white; padding: 2px 6px; border-radius: 3px; font-size: 11px; font-weight: bold; border: 1px solid #2196F3; white-space: nowrap; box-shadow: 0 2px 4px rgba(0,0,0,0.2); margin-top: -50px;">${displayName} (CENTER)</div>`,
            iconSize: [null, null]
          })
        }).addTo(labelsLayer);

        console.log(`Added center tract ${idx + 1}:`, displayName);
      });
    }

    // Convert Web Mercator to WGS84
    const toLatLon = (x, y) => {
      const lon = (x / 20037508.34) * 180;
      let lat = (y / 20037508.34) * 180;
      lat = 180 / Math.PI * (2 * Math.atan(Math.exp(lat * Math.PI / 180)) - Math.PI / 2);
      return [lat, lon];
    };

    // Multi-layer color palette
    const layerColors = {
      tracts: '#3388ff',     // Blue - primary tracts
      wells: '#e74c3c',      // Red - wells
      contracts: '#9b59b6',  // Purple - contracts
      dsus: '#27ae60',       // Green - DSUs
      blocks: '#95a5a6',     // Gray - blocks
      sections: '#7f8c8d',   // Dark gray - sections
      activeRigs: '#f39c12'  // Orange - active rigs
    };

    // Marker icon URLs by layer color
    const markerIcons = {
      tracts: 'blue',
      wells: 'red',
      contracts: 'violet',
      dsus: 'green',
      blocks: 'grey',
      sections: 'grey',
      activeRigs: 'orange'
    };

    // Helper function to get marker color based on dynamic symbology
    const getMarkerColorForFeature = (sourceLayer, props) => {
      // For tracts, map TractType to marker color
      if (sourceLayer === 'tracts' && props.TractType) {
        const typeColorMap = {
          'F': 'blue',      // Fee - primary ownership
          'O': 'violet',    // ORRI - royalty (purple)
          'N': 'green',     // Non-participating
          'U': 'grey',      // Undivided
          'Q': 'blue',      // Qualified (cyan → blue)
          'V': 'orange',    // Vendor
          'OA': 'violet',
          'RF': 'blue',
          'RC': 'violet'
        };
        return typeColorMap[props.TractType] || 'blue';
      }
      // For wells, map ENVWellStatus to marker color
      if (sourceLayer === 'wells' && props.ENVWellStatus) {
        const statusColorMap = {
          'PRODUCING': 'green',
          'DUC': 'yellow',
          'DRILLING': 'red',
          'PERMITTED': 'blue',
          'COMPLETED': 'blue',
          'INACTIVE PRODUCER': 'grey',
          'DRILLED': 'orange',
          'PERMIT EXPIRED': 'grey',
          'PERMIT CANCELLED': 'grey',
          'P & A': 'grey',
          'SHUT-IN': 'grey'
        };
        return statusColorMap[props.ENVWellStatus] || 'red';
      }
      // Fallback to layer-based color
      return markerIcons[sourceLayer] || 'red';
    };

    // Add polygons and markers for results
    features.forEach((feature, idx) => {
      const props = feature.attributes || feature.properties || {};
      // Use real TPL field names for display
      const displayName = props.TractID || props.WellName || props.API || props.BlkSec || props.SURVEYBLO3 || props.NAME || 'Unknown';

      // Get source layer for multi-layer styling
      const sourceLayer = feature._sourceLayer || layer;
      const featureColor = layerColors[sourceLayer] || '#FF6B6B';
      const markerColor = getMarkerColorForFeature(sourceLayer, props);

      // Handle ArcGIS Polygon format (geometry.rings)
      if (feature.geometry && feature.geometry.rings) {
        const rings = feature.geometry.rings;

        // Convert ArcGIS rings to Leaflet polygon coordinates [lat, lon]
        const latlngs = rings.map(ring =>
          ring.map(coord => [coord[1], coord[0]]) // Swap [lon, lat] to [lat, lon]
        );

        if (idx === 0) {
          console.log('ArcGIS Polygon format detected:', {
            rings: rings.length,
            firstRing: rings[0].length + ' points',
            displayName
          });
        }

        // Determine polygon style based on feature attributes (dynamic symbology)
        let polygonStyle = {
          color: featureColor,
          fillColor: featureColor,
          fillOpacity: 0.2,
          weight: 2
        };

        // Apply attribute-driven symbology for tracts (by TractType)
        if (sourceLayer === 'tracts' && props.TractType) {
          // Check for user overrides first
          const tractOverride = symbologyOverrides?.tractTypes?.[props.TractType];
          if (tractOverride) {
            polygonStyle = {
              color: tractOverride,
              fillColor: tractOverride,
              fillOpacity: 0.4,
              weight: 2
            };
          } else {
            polygonStyle = symbologyConfig.getTractStyle(props.TractType);
          }
        }
        // Apply attribute-driven symbology for wells (by ENVWellStatus)
        else if (sourceLayer === 'wells' && props.ENVWellStatus) {
          // Check for user overrides first
          const wellOverride = symbologyOverrides?.wellStatuses?.[props.ENVWellStatus];
          const baseStyle = symbologyConfig.getWellStyle(props.ENVWellStatus);
          polygonStyle = {
            color: wellOverride || baseStyle.color,
            fillColor: wellOverride || baseStyle.color,
            fillOpacity: 0.3,
            weight: 2
          };
        }

        // Override with proximity-specific styling if applicable
        if (isProximityQuery) {
          // Green for nearby tracts in proximity query
          polygonStyle.color = '#4CAF50';
          polygonStyle.fillColor = '#4CAF50';
        }

        // Create polygon with styling
        const polygon = L.polygon(latlngs, polygonStyle);

        const service = featureServices[layer];
        // Popup removed - using feature panel instead
        // polygon.bindPopup(generatePopupContent(props, service?.popupTemplate));
        polygon.addTo(polygonsGroup); // Add to polygons layer (not clustered)

        // Add click handler to polygon (use source layer for multi-layer)
        polygon.on('click', () => {
          if (onFeatureClick) {
            onFeatureClick(feature, sourceLayer || layer);
          }
        });

        // Add a pin marker at the centroid (this gets clustered)
        const centroid = polygon.getBounds().getCenter();
        const pinMarker = L.marker(centroid, {
          icon: L.icon({
            iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${markerColor}.png`,
            shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.4/images/marker-shadow.png',
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41]
          })
        });

        // Popup removed - using feature panel instead
        // pinMarker.bindPopup(generatePopupContent(props, service?.popupTemplate));
        pinMarker.addTo(markersGroup); // Add to cluster group

        // Add click handler to pin marker (use source layer for multi-layer)
        pinMarker.on('click', () => {
          if (onFeatureClick) {
            onFeatureClick(feature, sourceLayer || layer);
          }
        });

        // Add a text label near the pin (shown only at high zoom levels)
        // Include distance for proximity queries, or layer name for multi-layer
        let labelText = displayName;
        let labelBorderColor = polygonStyle.color; // Use dynamic symbology color

        // For multi-layer queries, add layer type to label
        const isMultiLayer = queryResults.isMultiLayer || queryResults.layer === 'multi';
        if (isMultiLayer && sourceLayer) {
          const layerLabel = sourceLayer === 'tracts' ? '📍' :
                            sourceLayer === 'wells' ? '🛢️' :
                            sourceLayer === 'contracts' ? '📋' :
                            sourceLayer === 'dsus' ? '🏗️' : '';
          labelText = `${layerLabel} ${displayName}`;
        }

        if (isProximityQuery && proximityData.nearbyTracts) {
          // Find this tract in nearbyTracts to get distance
          const nearbyTract = proximityData.nearbyTracts.find(nt => {
            const ntProps = nt.feature.attributes || nt.feature.properties || {};
            return (ntProps.TractID && ntProps.TractID === props.TractID) ||
                   (ntProps.WellName && ntProps.WellName === props.WellName);
          });

          if (nearbyTract) {
            const unit = proximityData.unit === 'kilometers' ? 'km' : 'mi';
            labelText = `${displayName} (${nearbyTract.distance} ${unit})`;
            labelBorderColor = '#4CAF50'; // Green border for proximity results
          }
        }

        const label = L.marker(centroid, {
          icon: L.divIcon({
            className: 'tract-label',
            html: `<div style="background: white; padding: 2px 6px; border-radius: 3px; font-size: 11px; font-weight: bold; border: 1px solid ${labelBorderColor}; white-space: nowrap; box-shadow: 0 2px 4px rgba(0,0,0,0.2); margin-top: -50px; cursor: pointer;">${labelText}</div>`,
            iconSize: [null, null]
          })
        });
        // Add click handler to label (same as pin marker)
        label.on('click', () => {
          if (onFeatureClick) {
            onFeatureClick(feature, sourceLayer || layer);
          }
        });
        label.addTo(labelsLayer); // Add to labels layer group

        console.log(`Adding polygon + pin ${idx}:`, displayName, `with ${latlngs[0].length} points`);
      }
      // Handle ArcGIS Point format (geometry.x, geometry.y)
      else if (feature.geometry && feature.geometry.x !== undefined && feature.geometry.y !== undefined) {
        // Check if already in WGS84 (lon: -180 to 180, lat: -90 to 90)
        const isWGS84 = Math.abs(feature.geometry.x) <= 180 && Math.abs(feature.geometry.y) <= 90;

        let latLng;
        if (isWGS84) {
          // Already WGS84 - just swap to [lat, lon] for Leaflet
          latLng = [feature.geometry.y, feature.geometry.x];
        } else {
          // Web Mercator - convert to WGS84
          latLng = toLatLon(feature.geometry.x, feature.geometry.y);
        }

        if (idx === 0) {
          console.log('ArcGIS Point format detected:', {
            x: feature.geometry.x,
            y: feature.geometry.y,
            isWGS84,
            latLng
          });
        }

        const marker = L.marker(latLng, {
          icon: L.icon({
            iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
            shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.4/images/marker-shadow.png',
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41]
          })
        });

        const service = featureServices[layer];
        // Popup removed - using feature panel instead
        // marker.bindPopup(generatePopupContent(props, service?.popupTemplate));
        marker.addTo(markersGroup);

        // Add click handler to marker
        marker.on('click', () => {
          if (onFeatureClick) {
            onFeatureClick(feature, layer);
          }
        });

        console.log(`Adding marker ${idx} at:`, latLng, 'for', displayName);
      }
      // Handle GeoJSON format (geometry.coordinates)
      else if (feature.geometry && feature.geometry.coordinates) {
        const coords = feature.geometry.coordinates;
        const geomType = feature.geometry.type;

        // Handle GeoJSON Polygon - real TPL tract data
        if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
          // Determine style based on layer
          const layerStyle = featureServices[layer]?.style || {};
          const isOutlineOnly = featureServices[layer]?.outlineOnly;

          const style = {
            color: layerStyle.color || '#FF6B6B',
            fillColor: isOutlineOnly ? 'transparent' : (layerStyle.fillColor || '#FF6B6B'),
            fillOpacity: isOutlineOnly ? 0 : (layerStyle.fillOpacity || 0.2),
            weight: layerStyle.weight || 2,
            dashArray: layerStyle.dashArray || null
          };

          // Create GeoJSON layer for polygon
          const geoJsonLayer = L.geoJSON(feature, { style });
          geoJsonLayer.addTo(polygonsGroup);

          // Add click handler
          geoJsonLayer.on('click', () => {
            if (onFeatureClick) {
              onFeatureClick(feature, layer);
            }
          });

          // Add pin marker at centroid (unless outline-only)
          if (!isOutlineOnly) {
            const bounds = geoJsonLayer.getBounds();
            const centroid = bounds.getCenter();

            const pinMarker = L.marker(centroid, {
              icon: L.icon({
                iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
                shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.4/images/marker-shadow.png',
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                popupAnchor: [1, -34],
                shadowSize: [41, 41]
              })
            });
            pinMarker.addTo(markersGroup);
            pinMarker.on('click', () => {
              if (onFeatureClick) {
                onFeatureClick(feature, layer);
              }
            });

            // Add label
            const label = L.marker(centroid, {
              icon: L.divIcon({
                className: 'tract-label',
                html: `<div style="background: white; padding: 2px 6px; border-radius: 3px; font-size: 11px; font-weight: bold; border: 1px solid ${style.color}; white-space: nowrap; box-shadow: 0 2px 4px rgba(0,0,0,0.2); margin-top: -50px; cursor: pointer;">${displayName}</div>`,
                iconSize: [null, null]
              })
            });
            // Add click handler to label
            label.on('click', () => {
              if (onFeatureClick) {
                onFeatureClick(feature, sourceLayer || layer);
              }
            });
            label.addTo(labelsLayer);
          }

          if (idx === 0) {
            console.log(`Adding GeoJSON ${geomType}:`, displayName, 'with style', style);
          }
        }
        // Handle GeoJSON LineString - well laterals
        else if (geomType === 'LineString' || geomType === 'MultiLineString') {
          // Apply dynamic symbology for wells based on ENVWellStatus
          let lineStyle;
          if (sourceLayer === 'wells' && props.ENVWellStatus) {
            // Check for user overrides first
            const wellOverride = symbologyOverrides?.wellStatuses?.[props.ENVWellStatus];
            const baseStyle = symbologyConfig.getWellStyle(props.ENVWellStatus);
            lineStyle = {
              color: wellOverride || baseStyle.color,
              weight: baseStyle.weight || 3
            };
          } else {
            const layerStyle = featureServices[layer]?.style || {};
            lineStyle = {
              color: layerStyle.color || '#e74c3c',
              weight: layerStyle.weight || 3
            };
          }

          // Create GeoJSON layer for line
          const geoJsonLayer = L.geoJSON(feature, { style: lineStyle });
          geoJsonLayer.addTo(polygonsGroup);

          // Add click handler
          geoJsonLayer.on('click', () => {
            if (onFeatureClick) {
              onFeatureClick(feature, layer);
            }
          });

          // Add marker at line center for clustering (with dynamic color)
          const bounds = geoJsonLayer.getBounds();
          const center = bounds.getCenter();
          const lineMarkerColor = getMarkerColorForFeature(sourceLayer, props);

          const lineMarker = L.marker(center, {
            icon: L.icon({
              iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${lineMarkerColor}.png`,
              shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.4/images/marker-shadow.png',
              iconSize: [20, 33],
              iconAnchor: [10, 33],
              popupAnchor: [1, -28],
              shadowSize: [33, 33]
            })
          });
          lineMarker.addTo(markersGroup);
          lineMarker.on('click', () => {
            if (onFeatureClick) {
              onFeatureClick(feature, layer);
            }
          });

          // Add well name label with dynamic color
          const label = L.marker(center, {
            icon: L.divIcon({
              className: 'well-label',
              html: `<div style="background: white; padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: bold; border: 1px solid ${lineStyle.color}; white-space: nowrap; box-shadow: 0 2px 4px rgba(0,0,0,0.2); margin-top: -40px; cursor: pointer;">${displayName}</div>`,
              iconSize: [null, null]
            })
          });
          // Add click handler to label
          label.on('click', () => {
            if (onFeatureClick) {
              onFeatureClick(feature, sourceLayer || layer);
            }
          });
          label.addTo(labelsLayer);

          if (idx === 0) {
            console.log(`Adding GeoJSON ${geomType} (well lateral):`, displayName);
          }
        }
        // Handle GeoJSON Point
        else if (geomType === 'Point') {
          const isWebMercator = Math.abs(coords[0]) > 200 || Math.abs(coords[1]) > 200;
          let latLng;
          if (isWebMercator) {
            latLng = toLatLon(coords[0], coords[1]);
          } else {
            latLng = [coords[1], coords[0]];
          }

          const marker = L.marker(latLng, {
            icon: L.icon({
              iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
              shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.4/images/marker-shadow.png',
              iconSize: [25, 41],
              iconAnchor: [12, 41],
              popupAnchor: [1, -34],
              shadowSize: [41, 41]
            })
          });

          marker.addTo(markersGroup);
          marker.on('click', () => {
            if (onFeatureClick) {
              onFeatureClick(feature, layer);
            }
          });

          console.log(`Adding GeoJSON Point ${idx} at:`, latLng, 'for', displayName);
        }
      }
    });

    // Smart zoom logic: Check if we should auto-zoom
    const shouldAutoZoom = () => {
      // Always zoom if zoom flag is true
      if (zoom) return true;
      if (features.length === 0) return false;

      // Smart auto-zoom: Check if new features are outside current viewport
      const currentBounds = mapRef.current.getBounds();
      const markersGroup = L.featureGroup(markersRef.current.getLayers());

      if (markersGroup.getLayers().length === 0) return false;

      const featureBounds = markersGroup.getBounds();

      // If feature bounds don't intersect with current view, auto-zoom
      const isOutsideView = !currentBounds.intersects(featureBounds);

      if (isOutsideView) {
        console.log('🎯 Smart auto-zoom: Features outside current viewport (county switch detected)');
        return true;
      }

      return false;
    };

    const needsZoom = shouldAutoZoom();

    // Zoom to extent if provided
    if (needsZoom && extent) {
      // Check if extent is already in WGS84
      const isWGS84 = Math.abs(extent.xmin) <= 180 && Math.abs(extent.ymin) <= 90;

      let sw, ne;
      if (isWGS84) {
        // Already WGS84 - use directly as [lat, lon]
        sw = [extent.ymin, extent.xmin];
        ne = [extent.ymax, extent.xmax];
      } else {
        // Web Mercator - convert to WGS84
        const toLatLon = (x, y) => {
          const lon = (x / 20037508.34) * 180;
          let lat = (y / 20037508.34) * 180;
          lat = 180 / Math.PI * (2 * Math.atan(Math.exp(lat * Math.PI / 180)) - Math.PI / 2);
          return [lat, lon];
        };
        sw = toLatLon(extent.xmin, extent.ymin);
        ne = toLatLon(extent.xmax, extent.ymax);
      }

      const bounds = [sw, ne];
      console.log('Zoom to bounds:', { isWGS84, extent, bounds });
      mapRef.current.fitBounds(bounds, { padding: [50, 50] });
    } else if (needsZoom && features.length > 0) {
      // If no extent but should zoom, fit to markers
      const group = L.featureGroup(markersRef.current.getLayers());
      mapRef.current.fitBounds(group.getBounds(), { padding: [50, 50] });
      console.log('Zooming to markers (smart auto-zoom or zoom flag)');
    } else {
      console.log('Not zooming:', { zoom, hasExtent: !!extent, featureCount: features.length });
    }

    // Function to update label visibility based on zoom
    const updateLabelVisibility = () => {
      const currentZoom = mapRef.current.getZoom();
      if (currentZoom >= 13) {
        // Add labels to map at high zoom
        if (!mapRef.current.hasLayer(labelsLayer)) {
          labelsLayer.addTo(mapRef.current);
        }
      } else {
        // Remove labels from map at low zoom
        if (mapRef.current.hasLayer(labelsLayer)) {
          mapRef.current.removeLayer(labelsLayer);
        }
      }
    };

    // Set initial visibility based on current zoom
    updateLabelVisibility();

    // Add zoom event listener
    const handleZoomEnd = () => {
      updateLabelVisibility();
    };

    mapRef.current.on('zoomend', handleZoomEnd);

    // Cleanup function to remove listener
    return () => {
      if (mapRef.current) {
        mapRef.current.off('zoomend', handleZoomEnd);
      }
    };

  }, [queryResults, symbologyOverrides]);

  // Layer toggles disabled - layers only show via query results
  const toggleLayer = (layerKey) => {
    // Feature layers are only displayed when queried
    console.log(`Layer "${layerKey}" can only be viewed through queries`);
  };

  return (
    <div className="map-wrapper">
      <div ref={mapContainerRef} className="map-container" style={{ height: '600px', width: '100%' }} />

      {/* Layer Control */}
      <div className="layer-control">
        <h3>Layers</h3>
        {Object.keys(featureServices).map(layerKey => {
          const service = featureServices[layerKey];
          const isChecked = layerVisibility[layerKey] || false;
          // Debug log for layer visibility
          if (isChecked) {
            console.log(`✅ Layer "${layerKey}" is checked:`, isChecked);
          }
          return (
            <label key={layerKey} className="layer-toggle">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => toggleLayer(layerKey)}
              />
              <span className="layer-dot" style={{ color: getLayerColor(layerKey) }}>●</span>
              <span className="layer-name">{service.name}</span>
            </label>
          );
        })}
      </div>

      {/* Results Info */}
      {queryResults && (
        <div className="results-info">
          <strong>{queryResults.count}</strong> features found
          {queryResults.count > 0 && queryResults.zoom && ' (zoomed to extent)'}
        </div>
      )}

      {/* Color Legend */}
      <MapLegend visibleLayers={layerVisibility} />
    </div>
  );
}

/**
 * Generate popup HTML content from feature properties
 */
function generatePopupContent(properties, template) {
  if (!template) {
    // Default: show all properties
    return Object.entries(properties)
      .map(([key, value]) => `<strong>${key}:</strong> ${value}`)
      .join('<br/>');
  }

  let content = `<div class="popup-content"><h3>${template.title}</h3>`;

  template.content.forEach(field => {
    const value = properties[field.fieldName];
    if (value !== null && value !== undefined) {
      content += `<p><strong>${field.label}:</strong> ${value}</p>`;
    }
  });

  content += '</div>';

  // Replace placeholders in title
  Object.keys(properties).forEach(key => {
    content = content.replace(`{${key}}`, properties[key]);
  });

  return content;
}

/**
 * Get color for layer
 */
function getLayerColor(layerKey) {
  const colors = {
    tracts: '#3388ff',     // Blue - primary tracts
    wells: '#e74c3c',      // Red - well laterals
    blocks: '#95a5a6',     // Gray - landgrid blocks
    sections: '#7f8c8d',   // Dark gray - survey sections
    contracts: '#9b59b6',  // Purple - easements/contracts
    dsus: '#27ae60',       // Green - development units
    activeRigs: '#f39c12'  // Orange - active rigs
  };
  return colors[layerKey] || '#95A5A6';
}

export default Map;
